import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import CategoryShortcuts from "./components/CategoryShortcuts";
import SectionHeading from "@/components/base/SectionHeading";
import DramaCard from "@/components/base/DramaCard";
import Pagination from "@/components/base/Pagination";
import { dramas } from "@/mocks/dramas";
import { newsList } from "@/mocks/news";
import { formatDate } from "@/utils/dramaUtils";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

export default function Home() {
  useSeo({
    title: "赏金女王影视大全 - 最新电视剧与影视资讯平台",
    description:
      "赏金女王网站为您提供最新电视剧、影视资讯和剧情介绍，涵盖丰富影视内容，方便用户了解影视动态。",
    keywords: "赏金女王,影视剧,电视剧,影视资讯,剧情介绍,影视大全,影视平台",
  });

  const featured = useMemo(
    () => [...dramas].sort((a, b) => b.popularity - a.popularity).slice(0, 5),
    []
  );

  const latest = useMemo(
    () =>
      dramas
        .filter((d) => d.category === "tv")
        .sort((a, b) => b.updateTime.localeCompare(a.updateTime)),
    []
  );

  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(latest.length / PAGE_SIZE);
  const pageItems = latest.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const hotNews = useMemo(
    () => [...newsList].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6),
    []
  );

  return (
    <>
      <HeroCarousel items={featured} />

      <div className="w-full px-4 md:px-8 py-10 md:py-14 space-y-12 md:space-y-16">
        <section className="max-w-3xl">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            赏金女王影视大全
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
            汇聚最新电视剧、电影作品与影视资讯，提供清晰的剧情介绍与丰富的分类筛选，让你轻松了解影视动态、快速找到想看的内容。
          </p>
        </section>

        <section>
          <SectionHeading title="分类快捷入口" subtitle="按内容类型快速找到你想看的影视作品" />
          <CategoryShortcuts />
        </section>

        <section>
          <SectionHeading
            title="最新电视剧推荐"
            subtitle="持续更新的热门剧集，第一时间掌握追剧清单"
            moreText="查看全部"
            morePath="/category/tv"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
          <div className="mt-8">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </section>

        <section>
          <SectionHeading
            title="影视资讯精选"
            subtitle="了解行业动态与剧集背后的故事"
            moreText="更多资讯"
            morePath="/news"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {hotNews.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex items-start gap-3 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-accent-300 hover:bg-background-100/60 transition-colors cursor-pointer"
              >
                <span className="w-10 h-10 shrink-0 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className="ri-newspaper-line" aria-hidden="true"></i>
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm md:text-base font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-500">
                    {formatDate(item.date)} · {item.category}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                关于赏金女王影视大全
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                赏金女王影视大全专注为影视爱好者提供权威及时的影视作品库、剧情介绍与行业资讯。我们覆盖最新电视剧与电影作品，
                提供丰富的分类筛选与搜索能力，帮助你快速找到感兴趣的内容。站内内容均用于影视资讯与剧情介绍参考，不涉及真实播放与下载。
              </p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <div className="flex-1 rounded-md bg-background-50 border border-primary-100 p-4 text-center">
                <p className="font-heading text-2xl font-bold text-primary-600">{dramas.length}+</p>
                <p className="mt-1 text-xs text-foreground-600">收录影视作品</p>
              </div>
              <div className="flex-1 rounded-md bg-background-50 border border-primary-100 p-4 text-center">
                <p className="font-heading text-2xl font-bold text-accent-600">{newsList.length}+</p>
                <p className="mt-1 text-xs text-foreground-600">影视资讯动态</p>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Link
              to="/about"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              了解更多
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}